import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { Mui } from '@leap4/frontend';
import { IComposedLine, IComposeLinesResult, ITrackedPublisher } from './appAdsLines.interface';
import {
    composeLinesForPublisher,
    errorMessage,
    fetchPublishersWithCounts,
    importLines,
} from './appAdsLines.api';

const {
    Alert,
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    LinearProgress,
    List,
    ListItem,
    ListItemText,
    TextField,
    Typography,
} = Mui;

/**
 * One selectable line in the dialog.
 *
 * Buyer and extra lines are seat-wide, so the same line serves every publisher. Our own line
 * is not: it's built from each publisher's own sellers.json seller ID, so there is one per
 * publisher and each is tied to the publisher it belongs to. `key` exists because of that -
 * two publishers' own lines have to be tickable separately even if the text collides.
 */
type GeneratedLine = IComposedLine & {
    key: string;
    publisherId?: string;
    publisherName?: string;
};

interface GenerateLinesModalProps {
    open: boolean;
    onClose: () => void;
    /**
     * When present, generation is scoped to that publisher and the picker is hidden. When
     * absent the picker appears and at least one publisher must be chosen - generation without
     * a publisher is no longer offered, because our own line can only be built from a
     * publisher's seller ID and lines with nowhere to go were a dead end.
     */
    publisherId?: string;
    publisherName?: string;
    onSaved?: (result: SaveResult) => void;
}

export interface SaveResult {
    added: number;
    skipped: number;
    publishers: number;
}

/**
 * What to tell the user after a save.
 *
 * Shared so both entry points say the same thing, and so "nothing new" reads as a result
 * rather than as a failure - saving lines every publisher already had is a successful no-op,
 * not an error, and silence would look like the button did nothing.
 */
export function describeSaveResult(r: SaveResult): string {
    const where = r.publishers === 1 ? 'the publisher' : `${r.publishers} publishers`;
    if (!r.added && !r.skipped) return 'Nothing was saved.';
    if (!r.added) return `Nothing new — all ${r.skipped} line(s) were already on ${where}.`;
    const skipped = r.skipped ? `, skipped ${r.skipped} already present` : '';
    return `Saved ${r.added} line(s) to ${where}${skipped}.`;
}

/**
 * The absorbed Lines Generator.
 *
 * It renders exactly what the standalone page rendered - our own line plus one card per buyer,
 * each PubAccId as a tickable line, cert IDs appended, descriptions shown, and each buyer's
 * curated extra lines - because it reads the same demand documents through the same query.
 *
 * Two entry points on purpose. The existing page lets you generate lines without choosing a
 * publisher at all, and removing that would delete a workflow people use today. From a
 * publisher's detail page you additionally get "save as tracked lines", which is the only part
 * that needs a publisher.
 */
export const GenerateLinesModal: React.FC<GenerateLinesModalProps> = ({
    open,
    onClose,
    publisherId,
    publisherName,
    onSaved,
}) => {
    const scoped = !!publisherId;

    const [selectedPublisherIds, setSelectedPublisherIds] = useState<string[]>([]);
    // The picker stages its own copy and only commits on Done, so choosing five publishers
    // recomposes once instead of five times - which is what made the dialog flash.
    const [pickerOpen, setPickerOpen] = useState(false);
    const [draftIds, setDraftIds] = useState<string[]>([]);
    const [pickerFilter, setPickerFilter] = useState('');
    const [ownLinesExpanded, setOwnLinesExpanded] = useState(false);
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [saving, setSaving] = useState(false);
    const [saveResult, setSaveResult] = useState<SaveResult | null>(null);
    const [error, setError] = useState('');

    const targetIds = useMemo(
        () => (scoped ? [publisherId] : selectedPublisherIds),
        [scoped, publisherId, selectedPublisherIds],
    );

    const { data: publishers } = useQuery('trackedPublishers', fetchPublishersWithCounts, {
        enabled: open && !scoped,
        refetchOnWindowFocus: false,
    });

    const nameFor = (pid: string) =>
        scoped ? publisherName : (publishers || []).find((p: ITrackedPublisher) => p._id === pid)?.name || pid;

    /**
     * Composed once per target publisher, in parallel.
     *
     * It has to be per publisher rather than once for the seat: the buyer lines come out the
     * same every time, but our own line is built from that publisher's seller ID, so a single
     * composition would put one publisher's own line on all of them. The key is sorted so
     * picking the same publishers in a different order reuses the cached result.
     */
    const { data: composedList, isLoading } = useQuery<IComposeLinesResult[]>(
        ['composeLines', [...targetIds].sort().join(',')],
        () => Promise.all(targetIds.map((pid) => composeLinesForPublisher(pid))),
        {
            enabled: open && targetIds.length > 0,
            refetchOnWindowFocus: false,
            // Holds the previous composition on screen while the new one loads, so adding a
            // publisher doesn't blank the dialog and reflow it.
            keepPreviousData: true,
        },
    );

    useEffect(() => {
        if (open) {
            setSelected(new Set());
            setSaveResult(null);
            setError('');
            if (!scoped) setSelectedPublisherIds([]);
            setPickerOpen(false);
            setPickerFilter('');
            setOwnLinesExpanded(false);
        }
    }, [open, scoped]);

    /**
     * Changing which publishers are targeted invalidates the ticks.
     *
     * Own-line ticks are keyed by publisher, so a publisher dropped from the selection would
     * otherwise leave a ticked line behind that no longer appears - and it would still be
     * counted in "N of M selected".
     */
    useEffect(() => {
        setSelected(new Set());
    }, [selectedPublisherIds]);

    /**
     * Clears the confirmation as soon as the selection changes.
     *
     * Otherwise "Saved 14 lines" would still be sitting there while the user ticks a different
     * set, describing a save that no longer matches what's on screen.
     */
    useEffect(() => {
        setSaveResult(null);
    }, [selected]);

    /** Our own line for each target publisher, in the order they were picked. */
    const ownLines: GeneratedLine[] = useMemo(() => {
        if (!composedList) return [];
        return targetIds
            .map((pid, i) => {
                const own = composedList[i]?.ownLine;
                if (!own) return null;
                return { ...own, key: `own:${pid}`, publisherId: pid, publisherName: nameFor(pid) };
            })
            .filter(Boolean) as GeneratedLine[];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [composedList, targetIds, publishers]);

    /** Publishers we couldn't build an own line for, with the reason - each is worth naming. */
    const ownLineProblems = useMemo(() => {
        if (!composedList) return [];
        return targetIds
            .map((pid, i) => {
                const result = composedList[i];
                if (!result || result.ownLine) return null;
                return { name: nameFor(pid), reason: result.ownLineUnavailableReason };
            })
            .filter(Boolean) as { name: string; reason?: string }[];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [composedList, targetIds, publishers]);

    /**
     * Buyer and extra lines, taken from the first composition.
     *
     * They're seat-wide - the same demands for every publisher - so reading them once avoids
     * showing the identical card N times.
     */
    const sharedLines: GeneratedLine[] = useMemo(() => {
        const first = composedList?.[0];
        if (!first) return [];
        return [...(first.buyerLines || []), ...(first.extraLines || [])].map((l) => ({ ...l, key: l.line }));
    }, [composedList]);

    const allLines: GeneratedLine[] = useMemo(() => [...ownLines, ...sharedLines], [ownLines, sharedLines]);

    /** Group buyer/extra lines by buyer, matching the card layout of the existing page. */
    const byBuyer = useMemo(() => {
        const groups = new Map<string, GeneratedLine[]>();
        sharedLines.forEach((l) => {
            const key = l.demandName || 'Other';
            if (!groups.has(key)) groups.set(key, []);
            groups.get(key).push(l);
        });
        return Array.from(groups.entries());
    }, [sharedLines]);

    const toggle = (key: string) =>
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });

    const toggleAll = () =>
        setSelected((prev) => (prev.size === allLines.length ? new Set() : new Set(allLines.map((l) => l.key))));

    const selectedText = allLines
        .filter((l) => selected.has(l.key))
        .map((l) => l.line)
        .join('\n');

    const copy = () => navigator.clipboard.writeText(selectedText);

    const exportTxt = () => {
        // One publisher gives the file a meaningful name; several would just make it wrong.
        const name = targetIds.length === 1 ? nameFor(targetIds[0]) : undefined;
        const blob = new Blob([selectedText], { type: 'text/plain' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `${name ? `${name}-` : ''}app-ads-lines.txt`;
        link.click();
        setTimeout(() => URL.revokeObjectURL(link.href), 2000);
    };

    /** The picker's list, filtered by the search box. Sorted by name for findability. */
    const pickerRows: ITrackedPublisher[] = useMemo(() => {
        const needle = pickerFilter.trim().toLowerCase();
        return (publishers || [])
            .filter(
                (p: ITrackedPublisher) =>
                    !needle ||
                    (p.name || '').toLowerCase().includes(needle) ||
                    (p.domain || '').toLowerCase().includes(needle),
            )
            .sort((a: ITrackedPublisher, b: ITrackedPublisher) => (a.name || '').localeCompare(b.name || ''));
    }, [publishers, pickerFilter]);

    const toggleDraft = (pid: string) =>
        setDraftIds((prev) => (prev.includes(pid) ? prev.filter((x) => x !== pid) : [...prev, pid]));

    /** Applies to what's currently filtered, so it never silently ticks rows you can't see. */
    const allShownChosen = pickerRows.length > 0 && pickerRows.every((p) => draftIds.includes(p._id));

    const toggleAllShown = () =>
        setDraftIds((prev) => {
            const shown = pickerRows.map((p) => p._id);
            if (allShownChosen) return prev.filter((pid) => !shown.includes(pid));
            return Array.from(new Set([...prev, ...shown]));
        });

    const allOwnChosen = ownLines.length > 0 && ownLines.every((l) => selected.has(l.key));
    const someOwnChosen = ownLines.some((l) => selected.has(l.key));

    const toggleAllOwn = () =>
        setSelected((prev) => {
            const next = new Set(prev);
            if (allOwnChosen) ownLines.forEach((l) => next.delete(l.key));
            else ownLines.forEach((l) => next.add(l.key));
            return next;
        });

    const asRow = (l: GeneratedLine) => ({
        domain: l.domain,
        pubId: l.pubId,
        relationship: l.relationship,
        certId: l.certId,
        // Provenance, so a generated line keeps its "why" once it's tracked - otherwise OTTA's
        // four ottadvisors.com lines become identical rows.
        sourceDemandId: l.demandId,
        sourceDescription: l.description,
    });

    /**
     * Saves per publisher rather than in one bulk call, because the rows differ: every
     * publisher gets the shared buyer lines, but only its own own-line.
     *
     * Sequential on purpose - the import endpoint dedupes against what the publisher already
     * has, and firing these in parallel would have them racing on the same seat.
     */
    const saveAsTracked = async () => {
        if (!targetIds.length) return;
        try {
            setSaving(true);
            setError('');

            const shared = sharedLines.filter((l) => selected.has(l.key)).map(asRow);
            let added = 0;
            let skipped = 0;

            for (const pid of targetIds) {
                const own = ownLines.find((l) => l.publisherId === pid && selected.has(l.key));
                const rows = own ? [asRow(own), ...shared] : shared;
                if (!rows.length) continue;
                const result = await importLines(pid, rows);
                added += result.added;
                skipped += result.skipped;
            }

            /**
             * Reported in the footer rather than in the scrolling content.
             *
             * The confirmation used to sit at the bottom of the dialog body, below every buyer
             * card - so with a dozen publishers selected it was hundreds of pixels below the
             * fold and the save looked like it had done nothing. DialogActions never scrolls,
             * and it's where the click just happened.
             *
             * The dialog deliberately stays open: Copy and Export TXT still apply to the same
             * selection, and closing would throw that away.
             */
            const result = { added, skipped, publishers: targetIds.length };
            setSaveResult(result);
            onSaved?.(result);
        } catch (e: any) {
            setError(errorMessage(e));
        } finally {
            setSaving(false);
        }
    };

    const lineRow = (l: GeneratedLine) => (
        <Box key={l.key} display="flex" alignItems="flex-start" gap={1}>
            <Checkbox
                size="small"
                checked={selected.has(l.key)}
                onChange={() => toggle(l.key)}
                sx={{ mt: '-2px' }}
            />
            <Box>
                <Typography sx={{ fontFamily: 'monospace', fontSize: 13, wordBreak: 'break-all' }}>{l.line}</Typography>
                {(l.description || l.publisherName) && (
                    <Typography variant="caption" color="text.secondary">
                        {l.publisherName ? `for ${l.publisherName}` : l.description}
                    </Typography>
                )}
            </Box>
        </Box>
    );

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>
                Generate lines{publisherName ? ` — ${publisherName}` : ''}
                {!scoped && targetIds.length > 1 ? ` — ${targetIds.length} publishers` : ''}
            </DialogTitle>

            <DialogContent dividers>
                {!scoped && (
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        gap={2}
                        mb={2}
                        p={1.5}
                        border="1px solid"
                        borderColor="divider"
                        borderRadius={1}
                    >
                        <Box minWidth={0}>
                            <Typography variant="subtitle2">
                                Publishers ({selectedPublisherIds.length})
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                                {selectedPublisherIds.length
                                    ? selectedPublisherIds.map((pid) => nameFor(pid)).join(', ')
                                    : 'None chosen yet'}
                            </Typography>
                        </Box>
                        <Button
                            variant="outlined"
                            size="small"
                            sx={{ flexShrink: 0 }}
                            onClick={() => {
                                setDraftIds(selectedPublisherIds);
                                setPickerFilter('');
                                setPickerOpen(true);
                            }}
                        >
                            {selectedPublisherIds.length ? 'Change' : 'Choose publishers'}
                        </Button>
                    </Box>
                )}

                {!scoped && !targetIds.length && (
                    <Typography variant="body2" color="text.secondary">
                        Pick at least one publisher to generate lines for. Our own line is built from each
                        publisher's seller ID, so there's nothing to generate until you choose.
                    </Typography>
                )}

                {isLoading && <LinearProgress />}

                {/* Named per publisher rather than as one message: with several selected, "not
                    linked to a sellers.json entry" is useless without saying which. */}
                {ownLineProblems.map((problem) => (
                    <Alert key={problem.name} severity="info" sx={{ mb: 2 }}>
                        <strong>{problem.name}</strong> — {problem.reason || 'our own line could not be built.'}
                    </Alert>
                ))}

                {/*
                    One checkbox rather than a line per publisher.
                    Our own line is deterministic - given a publisher, there is exactly one, and
                    no decision to make about it. The only real choice is whether to include it
                    at all, so that's the only control. Listing it per publisher meant 59 rows
                    of near-identical text for a single yes/no, which buried the buyer lines
                    underneath. The list is still there behind "Show lines" for checking one.
                */}
                {!!ownLines.length && (
                    <Box mb={2}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    size="small"
                                    checked={allOwnChosen}
                                    indeterminate={someOwnChosen && !allOwnChosen}
                                    onChange={toggleAllOwn}
                                />
                            }
                            label={
                                <Typography variant="subtitle2">
                                    {ownLines.length > 1
                                        ? `Add our line for each publisher (${ownLines.length})`
                                        : 'Add our line'}
                                </Typography>
                            }
                        />
                        {/* The label alone read as jargon - it isn't obvious that "our line" is
                            something the *publisher* has to add, nor why there's one each. */}
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4 }}>
                            The line each publisher needs in their own app-ads.txt to authorise us to sell their
                            inventory. The ID is that publisher's entry in our sellers.json, so it's different for
                            every one of them — which is why they can't share a single line.
                        </Typography>

                        {ownLines.length > 1 && (
                            <Button
                                size="small"
                                sx={{ ml: 3.5, mt: 0.5 }}
                                onClick={() => setOwnLinesExpanded((v) => !v)}
                            >
                                {ownLinesExpanded ? 'Hide lines' : 'Show lines'}
                            </Button>
                        )}

                        {(ownLinesExpanded || ownLines.length === 1) && (
                            <Box mt={1} ml={2}>
                                {ownLines.map(lineRow)}
                            </Box>
                        )}
                    </Box>
                )}

                {!!byBuyer.length && (
                    <>
                        <Divider sx={{ my: 2 }} />
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                            <Typography variant="subtitle2">Buyers</Typography>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={allLines.length > 0 && selected.size === allLines.length}
                                        onChange={toggleAll}
                                    />
                                }
                                label="Select all lines"
                            />
                        </Box>

                        <Box
                            display="grid"
                            gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }}
                            gap={2}
                        >
                            {byBuyer.map(([buyer, lines]) => (
                                <Box key={buyer} border="1px solid" borderColor="divider" borderRadius={1} p={1.5}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        {buyer}
                                    </Typography>
                                    {lines.map(lineRow)}
                                </Box>
                            ))}
                        </Box>
                    </>
                )}

                {!isLoading && !!targetIds.length && !allLines.length && (
                    <Typography variant="body2" color="text.secondary">
                        Nothing to generate. Buyer lines come from active buyers with publisher account IDs
                        configured.
                    </Typography>
                )}

                {error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {error}
                    </Alert>
                )}
            </DialogContent>

            <DialogActions>
                {saveResult ? (
                    <Typography
                        variant="caption"
                        color="success.main"
                        sx={{ mr: 'auto', pl: 2, fontWeight: 600 }}
                    >
                        {describeSaveResult(saveResult)}
                    </Typography>
                ) : (
                    <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto', pl: 2 }}>
                        {selected.size} of {allLines.length} selected
                    </Typography>
                )}
                <Button onClick={copy} disabled={!selected.size}>
                    Copy
                </Button>
                <Button onClick={exportTxt} disabled={!selected.size}>
                    Export TXT
                </Button>
                <Button
                    variant="contained"
                    onClick={saveAsTracked}
                    disabled={!selected.size || !targetIds.length || saving}
                    title={
                        !targetIds.length
                            ? 'Choose at least one publisher first'
                            : !selected.size
                            ? 'Tick the lines to save'
                            : `Add the selected lines to ${targetIds.length} publisher(s) as tracked lines`
                    }
                >
                    {targetIds.length > 1
                        ? `Save to ${targetIds.length} publishers`
                        : 'Save as tracked lines'}
                </Button>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>

            {/* A staged picker rather than a live multi-select.
                Committing on Done is what stops the flashing: every tick used to change the
                query key and recompose, so choosing five publishers meant five round trips and
                five reflows of the dialog behind it. It also gives Cancel something to mean. */}
            <Dialog open={pickerOpen} onClose={() => setPickerOpen(false)} fullWidth maxWidth="xs">
                <DialogTitle>Choose publishers</DialogTitle>
                <DialogContent dividers sx={{ p: 0 }}>
                    <Box p={2} pb={1}>
                        <TextField
                            fullWidth
                            size="small"
                            autoFocus
                            placeholder="Filter by name or domain"
                            value={pickerFilter}
                            onChange={(e) => setPickerFilter(e.target.value)}
                        />
                        <FormControlLabel
                            sx={{ mt: 1 }}
                            control={
                                <Checkbox
                                    size="small"
                                    checked={allShownChosen}
                                    onChange={toggleAllShown}
                                    disabled={!pickerRows.length}
                                />
                            }
                            label={`Select all (${pickerRows.length} shown)`}
                        />
                    </Box>

                    {/* Bounded and scrollable - the full list ran past the bottom of the screen. */}
                    <List dense sx={{ maxHeight: '45vh', overflowY: 'auto', px: 2, pt: 0 }}>
                        {pickerRows.map((p: ITrackedPublisher) => (
                            <ListItem key={p._id} disableGutters disablePadding>
                                <FormControlLabel
                                    sx={{ width: '100%' }}
                                    control={
                                        <Checkbox
                                            size="small"
                                            checked={draftIds.includes(p._id)}
                                            onChange={() => toggleDraft(p._id)}
                                        />
                                    }
                                    label={<ListItemText primary={p.name} secondary={p.domain} />}
                                />
                            </ListItem>
                        ))}
                        {!pickerRows.length && (
                            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                                No publishers match "{pickerFilter}".
                            </Typography>
                        )}
                    </List>
                </DialogContent>
                <DialogActions>
                    <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto', pl: 2 }}>
                        {draftIds.length} selected
                    </Typography>
                    {/* Clears every tick, not just the filtered ones - "clear selection" that
                        left some behind would be worse than not having it. */}
                    <Button onClick={() => setDraftIds([])} disabled={!draftIds.length}>
                        Clear selection
                    </Button>
                    <Button onClick={() => setPickerOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={() => {
                            setSelectedPublisherIds(draftIds);
                            setPickerOpen(false);
                        }}
                    >
                        Done
                    </Button>
                </DialogActions>
            </Dialog>
        </Dialog>
    );
};

export default GenerateLinesModal;
